import { Injectable } from '@angular/core';
import {
  CarePathwayRow,
  KeyValueRow,
  ResultRow,
  ScenarioRow,
  SummaryRow,
} from '../structured-input/structured-input.service';
import { Subject } from 'rxjs';
import {
  DEFAULT_SUBCAT_GRAPH_STYLE,
  TIME_HORIZON_KEY_1Y,
  TIME_HORIZON_KEY_2Y,
  TIME_HORIZON_KEY_3Y,
  TIME_HORIZON_KEY_5Y,
  TIME_HORIZON_SCENARIO_ROW_NAME,
} from '../../constants';

export interface ResultCategory {
  name: string;
  subcategories: ResultSubcategory[];
  matIcon: string;
}

export type SubcatGraphStyle = 'bar' | 'line';

export interface ResultSubcategory {
  name: string;
  titleCardColspan?: number;
  titleCardRowspan?: number;
  titleCardImgFileName?: string;
  values: ResultValue[];
}

export interface TimeSeriesMeta {
  title?: string;
  index?: number;
  colspan?: number;
  style?: SubcatGraphStyle;
  info?: string;
}

export interface PieChartMeta {
  title?: string;
  subtitle?: string;
  index?: number;
  colspan?: number;
  info?: string;
}

export interface DataPointMeta {
  title?: string;
  subtitle?: string;
  index?: number;
  colspan?: number;
  imgFileName?: string;
  info?: string;
}

export interface ResultValue {
  name: string;
  y1: number;
  y2: number;
  y3: number;
  y4: number;
  y5: number;
  unit?: string;
  subValues?: ResultValue[];
  timeSeries?: TimeSeriesMeta;
  pieChart?: PieChartMeta;
  dataPoint?: DataPointMeta;
}

export interface CarePathwayStep {
  name: string;
  subtitle?: string;
  icon: string;
  description?: string;
  impactCategories: ImpactCategory[];
}

export interface ImpactCategory {
  name: string;
  impacts: StepImpact[];
}

export interface StepImpact {
  cardIndex: number;
  name: string;
  description?: string;
  imageFileName: string;
  cardColspan?: number;
  subImpacts: StepSubImpact[];
  cardAccent: boolean;
}

export interface StepSubImpact {
  name: string;
  value: number;
  unit?: string;
  addPieChart?: boolean;
}

export type TimeHorizonKey =
  | ''
  | typeof TIME_HORIZON_KEY_1Y
  | typeof TIME_HORIZON_KEY_2Y
  | typeof TIME_HORIZON_KEY_3Y
  | typeof TIME_HORIZON_KEY_1Y
  | typeof TIME_HORIZON_KEY_5Y;

export interface ScenarioCategory {
  name: string;
  items: ScenarioItem[];
}

export interface ScenarioItem {
  name: string;
  options?: string[];
  defaultValue: string;
  unit?: string;
  modifiable: boolean;
  comment?: string;
}

export interface IntroInfo {
  title: string;
  htmlContent: string;
}

export interface ConfigItem {
  key: string;
  value: string;
}

export interface SummaryItemInfo {
  title: string;
  matIconName: string;
  htmlContent: string;
}

export interface SummaryItem {
  isInfo: boolean;
  info?: SummaryItemInfo;
  imageFileName?: string;
}

/**
 * Assumes compliance check is already performed, ensuring that data follows Excel standards.
 */
@Injectable({
  providedIn: 'root',
})
export class ResultDataService {
  resultCategories$: Subject<ResultCategory[]> = new Subject<
    ResultCategory[]
  >();
  carePathwaySteps$: Subject<CarePathwayStep[]> = new Subject<
    CarePathwayStep[]
  >();
  timeHorizon$: Subject<TimeHorizonKey> = new Subject<TimeHorizonKey>();
  scenarioCategories$: Subject<ScenarioCategory[]> = new Subject<
    ScenarioCategory[]
  >();
  introInfos$: Subject<IntroInfo[]> = new Subject<IntroInfo[]>();
  configItems$: Subject<ConfigItem[]> = new Subject<ConfigItem[]>();
  summaryItems$: Subject<SummaryItem[]> = new Subject<SummaryItem[]>();

  reformatResultData(
    resultRows: ResultRow[],
    scenarioRows: ScenarioRow[],
    carePathwayRows: CarePathwayRow[],
    introRows: KeyValueRow[],
    configRows: KeyValueRow[],
    summaryRows: SummaryRow[],
  ): void {
    this.reformatResultRows(resultRows);
    this.reformatScenarioRows(scenarioRows);
    this.reformatCarePathwayRows(carePathwayRows);
    this.reformatIntroRows(introRows);
    this.reformatConfigRows(configRows);
    this.reformatSummaryRows(summaryRows);
  }

  reformatResultRows(rows: ResultRow[]): void {
    const resultCategories: ResultCategory[] = [];
    let currentValuesIndexedByHierarchy: ResultValue[] = [];

    for (const row of rows) {
      let value = this._createValue(row.subcatValueName!, row);

      if (row.categoryName) {
        resultCategories.push({
          name: row.categoryName,
          subcategories: [
            {
              name: row.subcatName!,
              titleCardColspan: row.subcatTitleColspan
                ? parseInt(row.subcatTitleColspan)
                : undefined,
              titleCardRowspan: row.subcatTitleRowspan
                ? parseInt(row.subcatTitleRowspan)
                : undefined,
              titleCardImgFileName: row.subcatImgFileName,
              values: [value],
            },
          ],
          matIcon: this._getCategoryMatIcon(row.categoryName),
        });
        currentValuesIndexedByHierarchy[0] = value;
        continue;
      }

      const currentCat = resultCategories.at(-1)!;

      if (row.subcatName) {
        currentCat.subcategories.push({
          name: row.subcatName!,
          titleCardColspan: row.subcatTitleColspan
            ? parseInt(row.subcatTitleColspan)
            : undefined,
          titleCardRowspan: row.subcatTitleRowspan
            ? parseInt(row.subcatTitleRowspan)
            : undefined,
          titleCardImgFileName: row.subcatImgFileName,
          values: [value],
        });
        currentValuesIndexedByHierarchy[0] = value;
        continue;
      }

      const currentSubcat = currentCat.subcategories.at(-1)!;

      const hierarchy = parseInt(row.subcatValueHierarchy!);
      if (row.subcatValueName) {
        if (hierarchy == 0) {
          currentSubcat.values.push(value);
        } else {
          currentValuesIndexedByHierarchy[hierarchy - 1].subValues?.push(value);
        }

        currentValuesIndexedByHierarchy[hierarchy] = value;
      }
    }

    this.resultCategories$.next(resultCategories);
  }

  reformatScenarioRows(rows: ScenarioRow[]): void {
    this._reformatScenarioRowsTimeHorizon(rows);
    this._reformatScenarioRowsCategories(rows);
  }

  reformatCarePathwayRows(rows: CarePathwayRow[]): void {
    const carePathwaySteps: CarePathwayStep[] = [];

    for (const row of rows) {
      if (row.stepTitle) {
        const step: CarePathwayStep = {
          name: row.stepTitle as string,
          subtitle: row.stepSubtitle?.toString(),
          icon: row.stepMatIconName?.toString() ?? '',
          description: row.stepDescription?.toString(),
          impactCategories: row.impactCategoryName
            ? [
                {
                  name: row.impactCategoryName!.toString(),
                  impacts: [this._createStepImpact(row)],
                },
              ]
            : [],
        };
        carePathwaySteps.push(step);
        continue;
      }

      const currentStep = carePathwaySteps.at(-1)!;

      if (row.impactCategoryName) {
        currentStep.impactCategories.push({
          name: row.impactCategoryName,
          impacts: [this._createStepImpact(row)],
        });
        continue;
      }

      const currentCat = currentStep.impactCategories.at(-1)!;

      if (row.impactName) {
        currentCat.impacts.push(this._createStepImpact(row));
      }
    }

    this.carePathwaySteps$.next(carePathwaySteps);
  }

  reformatIntroRows(rows: KeyValueRow[]): void {
    const introInfos: IntroInfo[] = [];

    for (let row of rows) {
      introInfos.push({
        title: row.key!,
        htmlContent: row.value!,
      });
    }

    this.introInfos$.next(introInfos);
  }

  reformatConfigRows(rows: KeyValueRow[]): void {
    const configItems: ConfigItem[] = [];
    rows.forEach((row) => {
      configItems.push({
        key: row.key as string,
        value: row.value as string,
      });
    });
  }

  reformatSummaryRows(rows: SummaryRow[]): void {
    const summaryItems: SummaryItem[] = [];
    rows.forEach((row) => {
      if (row.imageFileName) {
        summaryItems.push({ isInfo: false, imageFileName: row.imageFileName });
        return;
      }
      summaryItems.push({
        isInfo: true,
        info: {
          title: row.title as string,
          matIconName: row.matIconName as string,
          htmlContent: row.htmlContent as string,
        },
      });
    });

    this.summaryItems$.next(summaryItems);
  }

  /**
   * Helper function to create a value object
   */
  private _createValue(name: string, row: ResultRow): ResultValue {
    const isPercent = row.subcatValueUnit?.toString() === '%';
    const factor = isPercent ? 100 : 1;

    const getValue = (value: unknown, field: string) => {
      const coerced = this._coerceNumber(
        value,
        `Result value '${name}' (${field})`,
      );
      return this._roundNumber(coerced * factor);
    };

    return {
      name,
      y1: getValue(row.subcatValueY1, 'y1'),
      y2: getValue(row.subcatValueY2, 'y2'),
      y3: getValue(row.subcatValueY3, 'y3'),
      y4: getValue(row.subcatValueY4, 'y4'),
      y5: getValue(row.subcatValueY5, 'y5'),
      unit: row.subcatValueUnit,
      subValues: [],
      timeSeries: {
        style:
          (row.subcatValueTimeSeriesGraphStyle as SubcatGraphStyle) ??
          DEFAULT_SUBCAT_GRAPH_STYLE,
        index: row.subcatValueTimeSeriesGraphIndex
          ? parseInt(row.subcatValueTimeSeriesGraphIndex, 10)
          : undefined,
        colspan: row.subcatValueTimeSeriesGraphColspan
          ? parseInt(row.subcatValueTimeSeriesGraphColspan, 10)
          : undefined,
        title: row.subcatValueTimeSeriesGraphTitle,
        info: row.subcatValueTimeSeriesGraphInfo,
      },
      pieChart: {
        title: row.subcatValuePieChartTitle,
        subtitle: row.subcatValuePieChartSubtitle,
        info: row.subcatValuePieChartInfo,
        index: row.subcatValuePieChartIndex
          ? parseInt(row.subcatValuePieChartIndex!, 10)
          : undefined,
        colspan: row.subcatValuePieChartColspan
          ? parseInt(row.subcatValuePieChartColspan, 10)
          : undefined,
      },
      dataPoint: {
        title: row.subcatValueDataPointCardTitle,
        subtitle: row.subcatValueDataPointCardSubtitle,
        index: row.subcatValueDataPointCardIndex
          ? parseInt(row.subcatValueDataPointCardIndex, 10)
          : undefined,
        colspan: row.subcatValueDataPointCardColspan
          ? parseInt(row.subcatValueDataPointCardColspan, 10)
          : undefined,
        imgFileName: row.subcatValueDataPointCardImgFileName,
        info: row.subcatValueDataPointCardInfo,
      },
    };
  }

  /**
   * Helper function to create a step impact object
   */
  private _createStepImpact(row: CarePathwayRow): StepImpact {
    const isPercent = [
      row.subImpact1Unit?.toString() === '%',
      row.subImpact2Unit?.toString() === '%',
    ];
    const factor = isPercent.map((val) => (val ? 100 : 1));

    const getValue = (value: unknown, valueIndex: 0 | 1, field: string) => {
      const coerced = this._coerceNumber(
        value,
        `SubImpact '${field}' for impact '${row.impactName}'`,
      );
      return this._roundNumber(coerced * factor[valueIndex]);
    };

    const impact: StepImpact = {
      cardIndex: parseInt(row.impactCardIndex!.toString(), 10),
      name: row.impactName!.toString(),
      description: row.impactDescription?.toString(),
      imageFileName: row.impactImageFileName as string,
      cardColspan: row.impactCardColspan
        ? parseInt(row.impactCardColspan.toString(), 10)
        : undefined,
      subImpacts: [
        {
          name: row.subImpact1Name!.toString(),
          value: getValue(row.subImpact1Value, 0, 'subImpact1'),
          unit: row.subImpact1Unit?.toString(),
          addPieChart: !!row.subImpact1AddPieChart,
        },
      ],
      cardAccent: !!row.impactCardAccent,
    };

    // This solution works currently because we have a static either 1 or 2 sub impacts.
    // An alternative solution may be necessary if more subImpacts are implemented.
    if (row.subImpact2Name) {
      impact.subImpacts.push({
        name: row.subImpact2Name!.toString(),
        value: getValue(row.subImpact2Value, 1, 'subImpact2'),
        unit: row.subImpact2Unit?.toString(),
        addPieChart: !!row.subImpact2AddPieChart,
      });
    }

    return impact;
  }

  private _reformatScenarioRowsTimeHorizon(rows: ScenarioRow[]): void {
    const timeHorizon = rows.find(
      (row) => row.name === TIME_HORIZON_SCENARIO_ROW_NAME,
    )?.defaultValue as TimeHorizonKey;

    if (!timeHorizon) return;
    this.timeHorizon$.next(timeHorizon);
  }

  private _reformatScenarioRowsCategories(rows: ScenarioRow[]) {
    let scenarioCategories: ScenarioCategory[] = [];

    for (let i = 0; i < rows.length; i++) {
      if (rows[i].category) {
        scenarioCategories.push({
          name: rows[i].category!,
          items: [this._createScenarioItem(rows[i])],
        });
        continue;
      }

      scenarioCategories.at(-1)?.items.push(this._createScenarioItem(rows[i]));
    }

    this.scenarioCategories$.next(scenarioCategories);
  }

  private _createScenarioItem(row: ScenarioRow) {
    return {
      name: row.name,
      options: row.options,
      defaultValue: row.defaultValue as string,
      unit: row.unit,
      modifiable: row.modifiable,
      comment: row.comment,
    };
  }

  private _roundNumber(num: number) {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  private _coerceNumber(raw: unknown, context: string): number {
    const num = Number.parseFloat(raw as any);
    if (!Number.isFinite(num)) {
      console.error(
        `[HF] Non-numeric value encountered while mapping results: ${context}`,
        raw,
      );
      return num;
    }
    return num;
  }

  private _getCategoryMatIcon(
    catName: string,
  ): 'payments' | 'group_work' | 'atr' | 'question_mark' {
    const catLower = catName.toLowerCase();

    if (catLower.includes('budgétaire')) {
      return 'payments';
    }
    if (catLower.includes('organisationnel') && catLower.includes('relative')) {
      return 'group_work';
    }
    if (catLower.includes('organisationnel')) {
      return 'atr';
    }

    return 'question_mark';
  }
}
